import { useQuery } from "@tanstack/react-query";
import { TaskStatusEnum } from "enums/TaskStatusEnum";
import { toServerDate } from "helpers";
import { createLogger } from "logging";
import { PatchTaskModel } from "models/PatchTaskModel";
import { useApi, usePermissions, useTask, useToasts, useTranslations } from "providers";
import { useMeeting } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { EditTaskContext, getTaskFieldValues, TaskFieldValues } from "./EditTaskContext";

const logger = createLogger("EditTaskContext");

export const EditTaskProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleHttpError } = usePermissions();
  const { reloadMeeting } = useMeeting();
  const { setTaskUpdating } = useTask();
  const { tasksApi } = useApi();
  const i18n = useTranslations();
  const toasts = useToasts();

  const [confirmTaskUnsaved, setConfirmTaskUnsaved] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [editTaskId, setEditTaskId] = useState<number>(-1);

  const form = useForm<TaskFieldValues>({
    defaultValues: undefined,
  });
  const { formState, getValues, reset } = form;

  // Load task using React Query
  const queryResult = useQuery({
    queryKey: [`Task|${editTaskId}|Edit`],
    queryFn: async () => {
      const data = await tasksApi.getItem(editTaskId);
      const fieldValues = getTaskFieldValues(data);
      reset(fieldValues);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: editTaskId > 0,
    refetchOnWindowFocus: false,
  });

  const { error } = queryResult;
  const isLoading = queryResult.isFetching;

  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  const watchErrors = JSON.stringify(formState.errors, null, 2);
  const { isDirty, isValid } = formState;
  const isSubmittable = useMemo(() => isValid || watchErrors == "{}", [isValid, watchErrors]);

  const handleClose = useCallback(
    (ignoreDirty?: boolean) => {
      const confirm = isDirty && !ignoreDirty;
      logger.debug(`handleClose(): isDirty:=${isDirty} (ignore:=${ignoreDirty}) -> ${confirm}`);
      if (confirm) {
        setConfirmTaskUnsaved(true);
      } else {
        setIsSubmitting(false);
        setEditTaskId(-1);
      }
    },
    [isDirty]
  );

  const handleSuccess = useCallback(
    (title: string) => {
      toasts.updateSuccess(i18n.Task, title);
      setIsSubmitting(false);
      setEditTaskId(-1);
      reloadMeeting();
    },
    [i18n, toasts, reloadMeeting]
  );

  const handleError = useCallback(
    (title: string, error: unknown) => {
      toasts.updateFailure(i18n.Task, title, error);
      throw error;
    },
    [i18n, toasts]
  );

  const getPatch = useCallback(
    (values: TaskFieldValues): PatchTaskModel => {
      const { title, assignedToId, description, status, source } = values;
      const startDate = toServerDate(values.startDate);
      const dueDate = toServerDate(values.dueDate);
      const patch: PatchTaskModel = {
        id: editTaskId,
        title,
        description,
        source,
        assignedTo: assignedToId,
        startDate,
        dueDate,
        status: TaskStatusEnum[status],
      };
      return patch;
    },
    [editTaskId]
  );

  const handleSave = useCallback(async () => {
    logger.debug(`handleSave():`, editTaskId, isSubmittable);
    if (!editTaskId) return;
    if (!isSubmittable) return;

    try {
      setIsSubmitting(true);
      setTaskUpdating(editTaskId, true);

      const patch = getPatch(getValues());
      const { title = "" } = patch;

      logger.debug(`Saving task with title: ${patch.title}`);

      try {
        await tasksApi.update(patch);
        handleSuccess(title);
      } catch (error) {
        handleError(title, error);
      }
    } catch (error) {
      logger.error("Error saving task:", error);
    } finally {
      setIsSubmitting(false);
      setTaskUpdating(editTaskId, false);
    }
  }, [editTaskId, isSubmittable, setTaskUpdating, getPatch, getValues, tasksApi, handleSuccess, handleError]);

  const contextValue = useMemo(
    () => ({
      confirmTaskUnsaved,
      setConfirmTaskUnsaved,
      editTaskId,
      setEditTaskId,
      isLoading,
      isDirty,
      isValid,
      isSubmittable,
      isSubmitting,
      form,
      getPatch,
      handleSave,
      handleClose,
    }),
    [
      confirmTaskUnsaved,
      editTaskId,
      isLoading,
      isDirty,
      isValid,
      isSubmittable,
      isSubmitting,
      form,
      getPatch,
      handleSave,
      handleClose,
    ]
  );

  return <EditTaskContext.Provider value={contextValue}>{children}</EditTaskContext.Provider>;
};
